import { useEffect } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '../Tooltip';
import { useUserSettings } from '../../shared';
import { CollapserIcon } from './CollapserIcon';

type CardSize = 'small' | 'medium' | 'unset';

type CardProps = {
  name?: string;
  title?: string;
  tooltip?: string;
  subtitle?: string;

  size?: CardSize;
  border?: string | boolean;
  children: React.ReactNode;
  actions?: React.ReactNode[];

  collapsable?: boolean;
  defaultCollapsed?: boolean;
  withOverflow?: boolean;

  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
};

const sizes: { [x in CardSize]: string } = {
  unset: '',
  small: 'w-[20rem]',
  medium: 'w-[48rem]',
};

export const Card: React.FC<CardProps> = ({
  name,
  title,
  subtitle,
  tooltip,
  children,
  size = 'medium',
  collapsable = false,
  defaultCollapsed = true,
  border = 'border shadow-sm',

  className,
  headerClassName,
  contentClassName,

  actions,
  withOverflow = true,
  disabled,
}) => {
  const [collapsed, setCollapsed] = useUserSettings(
    collapsable ? defaultCollapsed : false,
    name
  );
  const hasTitle = !!title?.length || !!subtitle?.length || actions?.length;

  useEffect(() => {
    if (disabled && collapsable) {
      setCollapsed(true);
    }
  }, [disabled, collapsable, setCollapsed]);

  return (
    <div
      className={twMerge(
        clsx(
          border,
          sizes[size],
          withOverflow ? 'max-h-screen' : '',
          disabled && 'opacity-50 pointer-events-none select-none'
        ),
        'flex flex-col bg-contrast rounded-card p-lg',
        className
      )}
    >
      {hasTitle && (
        <div
          onClick={
            collapsable && !disabled ? () => setCollapsed(c => !c) : undefined
          }
          className={clsx(
            headerClassName,
            'flex flex-col sm:flex-row justify-between items-start sm:items-center gap-y-sm',
            collapsable ? 'cursor-pointer' : ''
          )}
        >
          <div className="flex flex-col gap-y-sm">
            {!!title?.length && (
              <div className="flex gap-x-sm items-center text-big font-semibold text-mainText">
                {title}
                {!!tooltip && <Tooltip content={tooltip} />}
              </div>
            )}
            {!!subtitle?.length && (
              <div className="text-small font-normal text-secondary">
                {subtitle}
              </div>
            )}
          </div>

          <div
            className="shrink-0 flex flex-wrap gap-2 items-center sm:justify-end mt-sm sm:mt-0"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {actions &&
              actions.map((action, i) => (
                <div key={i} className="max-w-full">
                  {action}
                </div>
              ))}
            {collapsable && (
              <CollapserIcon
                show={!collapsed}
                onClick={
                  collapsable && !disabled
                    ? () => setCollapsed(c => !c)
                    : undefined
                }
              />
            )}
          </div>
        </div>
      )}

      <div
        className={clsx(
          contentClassName,
          'flex-grow',
          hasTitle ? 'mt-sm' : '',
          collapsed ? 'hidden' : '',
          withOverflow ? 'overflow-y-auto' : ''
        )}
      >
        {children}
      </div>
    </div>
  );
};
